/**
 * Illustration Component
 *
 * A unified component for displaying illustrations with text and actions.
 * Supports both empty states and error states with appropriate styling and imagery.
 */

import { EmptyImage, ErrorImage } from '@core/assets';

import * as styles from './Illustration.css';

export enum IllustrationType {
  EMPTY = 'empty',
  ERROR = 'error',
}

export enum IllustrationLayout {
  CENTERED = 'centered',
  HORIZONTAL = 'horizontal',
}

export interface IllustrationProps {
  /**
   * The type of illustration to display
   */
  type: IllustrationType;

  /**
   * The layout style for the illustration
   * @default IllustrationLayout.CENTERED
   */
  layout?: IllustrationLayout;

  /**
   * The main title text to display
   */
  title?: string;

  /**
   * Optional description text that appears below the title
   */
  description?: string;

  /**
   * Optional action button or custom content
   */
  action?: React.ReactNode;

  /**
   * Optional retry callback for error states
   */
  onRetry?: () => void;

  /**
   * Additional CSS class name
   */
  className?: string;
}

/**
 * Get default props based on illustration type
 */
function getDefaultProps(type: IllustrationType) {
  switch (type) {
    case IllustrationType.EMPTY:
      return {
        title: 'No data available',
        description: "There's nothing to show here yet.",
        image: EmptyImage,
        altText: 'No data available',
      };
    case IllustrationType.ERROR:
      return {
        title: 'Something went wrong',
        description: 'We encountered an error while loading your data. Please try again.',
        image: ErrorImage,
        altText: 'Error illustration',
      };
    default:
      return {
        title: 'No data available',
        description: "There's nothing to show here yet.",
        image: EmptyImage,
        altText: 'No data available',
      };
  }
}

/**
 * Illustration Component
 */
export function Illustration({ type, layout = IllustrationLayout.CENTERED, title, description, action, onRetry, className }: IllustrationProps) {
  const defaults = getDefaultProps(type);
  const finalTitle = title || defaults.title;
  const finalDescription = description || defaults.description;

  const containerClass = layout === IllustrationLayout.HORIZONTAL ? styles.horizontalContainer : styles.container;

  return (
    <div className={`${containerClass} ${className || ''}`}>
      <div className={styles.imageContainer}>
        <img alt={defaults.altText} className={styles.image} src={defaults.image} />
      </div>

      <div className={styles.content}>
        <h3 className={styles.title}>{finalTitle}</h3>
        {finalDescription && <p className={styles.description}>{finalDescription}</p>}

        {/* Show retry button for errors if onRetry is provided */}
        {type === IllustrationType.ERROR && onRetry && (
          <button className={styles.retryButton} onClick={onRetry}>
            Try Again
          </button>
        )}

        {/* Show custom action if provided */}
        {action && <div className={styles.action}>{action}</div>}
      </div>
    </div>
  );
}
